import { Component, OnInit, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { EdiService } from '../../../services/edi/edi.service';
import { GungModalService } from '../../../services/gung-modal/gung-modal.service';

type UploadConfigurationOption = {
  id: string;
  enabled: boolean;
  label: string;
};

@Component({
    selector: 'lib-edi-import-xml',
    templateUrl: './edi-import-xml.component.html',
    styleUrls: ['./edi-import-xml.component.css'],
    standalone: false
})
export class EdiImportXmlComponent implements OnInit {
  protected ediService = inject(EdiService);
  protected gungModalService = inject(GungModalService);
  protected translateService = inject(TranslateService);

  loading = false;
  loadingConfigurations = false;
  selectedConfigId = '';
  configurations: UploadConfigurationOption[] = [];
  selectedConfigWarning = '';

  ngOnInit(): void {
    this.loadConfigurations();
  }

  onConfigIdChange(event: Event) {
    const select = event.target as HTMLSelectElement;
    this.selectedConfigId = select.value;
    this.updateSelectedConfigWarning();
  }

  uploadFile(fileInput: HTMLInputElement) {
    const file = fileInput.files?.[0];
    if (!file || !this.selectedConfigId) {
      return;
    }

    this.loading = true;
    this.ediService.uploadAndProcessOrderMessage(this.selectedConfigId, file).subscribe(
      _data => {
        const msg = 'Order successfully imported and processed';
        this.gungModalService.openConfirmYesNoModal(undefined, msg, { size: 'sm' }, 'OK', null).then(_ => {});
        this.loading = false;
        fileInput.value = '';
      },
      _error => {
        const msg = this.getUploadErrorMessage(_error);
        this.gungModalService.openConfirmYesNoModal(undefined, msg, { size: 'sm' }, 'OK', null).then(_ => {});
        this.loading = false;
      }
    );
  }

  protected loadConfigurations() {
    this.loadingConfigurations = true;
    this.ediService.getOrderUploadConfigurations(false).subscribe(
      configurations => {
        this.configurations = (configurations || [])
          .map(configuration => this.toConfigurationOption(configuration))
          .filter((configuration): configuration is UploadConfigurationOption => !!configuration);
        if (!this.selectedConfigId && this.configurations.length) {
          const firstEnabled = this.configurations.find(configuration => configuration.enabled);
          this.selectedConfigId = firstEnabled ? firstEnabled.id : this.configurations[0].id;
        }
        this.updateSelectedConfigWarning();
        this.loadingConfigurations = false;
      },
      _error => {
        const msg = this.translateService.instant('ERROR');
        this.gungModalService.openConfirmYesNoModal(undefined, msg, { size: 'sm' }, 'OK', null).then(_ => {});
        this.loadingConfigurations = false;
      }
    );
  }

  protected toConfigurationOption(configuration: any): UploadConfigurationOption | null {
    if (typeof configuration === 'string' && configuration.trim().length) {
      return {
        id: configuration,
        enabled: true,
        label: configuration
      };
    }

    const candidates = [
      configuration?.configId,
      configuration?.id,
      configuration?.ediConfigId,
      configuration?.name,
      configuration?.key
    ];
    const match = candidates.find(value => typeof value === 'string' && value.trim().length);
    if (!match) {
      return null;
    }

    const enabled = this.toEnabled(configuration);
    return {
      id: match,
      enabled,
      label: enabled ? match : `${match} (not enabled)`
    };
  }

  protected toEnabled(configuration: any): boolean {
    const enabledCandidates = [configuration?.enabled, configuration?.isEnabled];
    const explicitBoolean = enabledCandidates.find(value => typeof value === 'boolean');
    if (typeof explicitBoolean === 'boolean') {
      return explicitBoolean;
    }
    return true;
  }

  protected updateSelectedConfigWarning() {
    const selectedConfiguration = this.configurations.find(configuration => configuration.id === this.selectedConfigId);
    this.selectedConfigWarning =
      selectedConfiguration && !selectedConfiguration.enabled
        ? 'Warning: selected configuration is not enabled. Manual upload may still be processed.'
        : '';
  }

  protected getUploadErrorMessage(error: any): string {
    const fallback = this.translateService.instant('ERROR');
    const errorBody = error?.error;

    if (typeof errorBody === 'object' && errorBody !== null) {
      return this.cleanErrorMessage(errorBody.gungMessage || errorBody.message || errorBody.error, fallback);
    }

    if (typeof errorBody === 'string') {
      const messageFromJson = this.tryParseErrorMessage(errorBody);
      return this.cleanErrorMessage(messageFromJson || errorBody, fallback);
    }

    return this.cleanErrorMessage(error?.message, fallback);
  }

  protected tryParseErrorMessage(errorBody: string): string | null {
    try {
      const parsed = JSON.parse(errorBody);
      if (parsed && typeof parsed === 'object') {
        return parsed.gungMessage || parsed.message || parsed.error || null;
      }
      return null;
    } catch {
      return null;
    }
  }

  protected cleanErrorMessage(message: unknown, fallback: string): string {
    if (typeof message !== 'string') {
      return fallback;
    }

    const firstLine = message.split('\n')[0].trim();
    return firstLine || fallback;
  }

  get selectedConfiguration(): UploadConfigurationOption | null {
    if (!this.selectedConfigId) {
      return null;
    }

    return this.configurations.find(configuration => configuration.id === this.selectedConfigId) || null;
  }

  get enabledConfigurationsCount(): number {
    return this.configurations.filter(configuration => configuration.enabled).length;
  }

  get disabledConfigurationsCount(): number {
    return this.configurations.length - this.enabledConfigurationsCount;
  }
}
